/// <reference path="../../../../../../shared/utils/View.ts"/>
/// <reference path="../../../app.d.ts"/>
/// <reference path="../../../../../../shared/models/generated/MitgliedschaftAnlageResponseApi.d.ts"/>
/// <reference path="../../../../../../shared/models/generated/WalletProxyQrCodeResponseModelApi.d.ts"/>
/// <reference path="../../../../../../shared/models/generated/WalletProxyUrlResponseModelApi.d.ts"/>

import { BasicMglConnectionService } from '../../../../../../shared/services/BasicMglConnectionService';
import { BasicApplicationUrlBuilder } from "../../../../../../shared/services/BasicApplicationUrlBuilder";
import { ApplicationMglInformationModel } from '../../../../../../shared/models/transport/ApplicationMglInformation';
import { AppPersistenceService } from '../../services/AppPersistenceService';
import { AppViewService } from '../../services/AppViewService';
import { AppValidationService } from '../../services/AppValidationService';
import { User } from "../../../../../../shared/models/api/User/User";
import { BankAccountService } from '../../../../../../shared/services/BankAccountService';
import { AppApiService } from '../../services/AppApiService';
import { CooperationService } from '../../../../../../shared/services/CooperationService';
import { SparteType } from '../../../../../../shared/models/api/SparteType';
import { AppModelAdapter } from '../../helper/AppModelAdapter';
import { ContractInfo } from '../../../../../../shared/models/api/User/ContractInfo';
import { SessionStorageKeys } from '../../Constants';
import { AppUtils } from '../../../../../../shared/utils/index';
import { PiwikTrackingService } from '../../../../../../shared/services/PiwikTrackingService';

export class ConfirmationPageController implements StepController {
	private readonly options: MglModularSystemV2AppOptions;
	private readonly user: User;
	private readonly successResponse: AnlageResponseApi;
	private readonly bankAccountService: BankAccountService;
	private readonly appViewService: AppViewService;
	private readonly appApiService: AppApiService;
	private readonly appPersistenceService: AppPersistenceService;
	private readonly cooperationService: CooperationService<MglModularSystemV2AppDefinition>;
	private readonly mglConnectionService: BasicMglConnectionService<MglModularSystemV2AppDefinition>;
	private readonly applicationUrlBuilder: BasicApplicationUrlBuilder<MglModularSystemV2AppDefinition>;
	private readonly piwikTrackingService: PiwikTrackingService;
	private adacpayValidationService: AppValidationService;

	private readonly parent: AppController;
	private processId: number;

	constructor(parent: AppController, options: MglModularSystemV2AppOptions, user: User, successResponse: ApiResponse<AnlageResponseApi>) {
		this.parent = parent;
		this.options = options;
		this.user = user;
		this.successResponse = successResponse.Data || {} as AnlageResponseApi;
		this.bankAccountService = new BankAccountService(options, user);
		this.appApiService = new AppApiService(options);
		this.appViewService = new AppViewService(options, undefined);
		this.appPersistenceService = new AppPersistenceService(options);
		if (this.options.app.iscooperation) {
			this.cooperationService = new CooperationService(options);
		}
		if (this.options.app.hasfdlmodule) {
			this.mglConnectionService = new BasicMglConnectionService(this.options);
			this.piwikTrackingService = new PiwikTrackingService();
			this.applicationUrlBuilder = new BasicApplicationUrlBuilder(this.options);
		}
	}

	public async start(): Promise<void> {
		const viewConfig = {
			data: await this.getViewOptions(),
			templateurl: this.options.app.currenttemplate.templateurl,
			parent: this,
			$ctx: this.options.$ctx,
			callback: this.afterRenderApp,
			options: this.options
		};

		this.appViewService.render(viewConfig);
	}

	/**
	 * returns view options for confirmation page.
	 * only variables defined in here are available in the view to use with handlebars.
	 */
	private async getViewOptions(): Promise<object> {
		const appModel = this.appPersistenceService.getApplicationModel();
		const bankdaten = this.bankAccountService.getBankdaten();
		const cooperationModel = this.options.app.iscooperation ? this.cooperationService.getDynamicFieldsFromStore() : {};
		const isFutureMglBegin = moment(appModel.FormModel.MitgliedschaftStartDatum).month() > moment().month();
		const advertisingData = this.appPersistenceService.getFriendsAdvertisingDataFromStorage();
		const advertisingConsent = this.appPersistenceService.getAdvertisingConsent();

		const advertisingKey = appModel.FormModel.Werbeschluessel ? appModel.FormModel.Werbeschluessel : (this.options.app.advertisingkey ? Number(this.options.app.advertisingkey) : undefined);
		const showDigitalClubcard = appModel.FormModel.Partner === undefined && appModel.FormModel.Kinder.length === 0 && (advertisingKey === 7001 || advertisingKey === 1076);

		return {
			IsConfirmationPage: true,
			FormModel: appModel.FormModel,
			Antragssteller: AppModelAdapter.mergeAntragssteller(this.successResponse, appModel.FormModel.Antragssteller),
			Partner: this.successResponse.Partner ? AppModelAdapter.getPartnerViewModel(this.successResponse.Partner) : undefined,
			Kinder: this.successResponse.Kinder,
			JungeErwachsene: this.successResponse.JungeErwachsene,
			User: this.user,
			TarifBeschreibung: ContractInfo.getDescription(this.options.app.ratenamemappings, appModel.Tarif.TarifCodeId),
			Beitrag: appModel.Tarif.Beitrag,
			productActiveModel: advertisingData.productActiveModel,
			hasCopayment: this.appPersistenceService.hasCopayment(),
			isCashItem: advertisingData.productIsCashItem,
			IsFutureMglBegin: isFutureMglBegin,
			Bankdaten: bankdaten,
			iscooperation: this.options.app.iscooperation !== undefined ? this.options.app.iscooperation : false,
			CooperationModel: cooperationModel,
			hideconfirmpageprintbutton: this.options.app.hideconfirmpageprintbutton,
			hidelegalnotice: this.options.app.hidelegalnotice,
			IsProfiWerber: appModel.FormModel.Profiwerbernummer && appModel.FormModel.Profiwerbernummer.length > 0,
			IstSchwerbehindert: appModel.FormModel.IstSchwerbehindert || false,
			IsYoungGenerationNoviceDriverLicense: AppModelAdapter.isYoungGenerationNoviceDriverLicense(appModel.Tarif.TarifCode),
			Bonus: appModel.FormModel.Bonus,
			registerurl: await T.Utils.Auth.makeRegisterURL(),
			apiretrieveregisterjwt: this.options.app.apiretrieveregisterjwt,
			AdvertisingConsent: advertisingConsent,
			showDigitalClubcard: showDigitalClubcard
		};
	}

	public afterRenderApp(): void {
		T.Utils.Tracking.pushKameleoonData();
		const mglApp = this.appPersistenceService.getApplicationModel();
		// we need to store this for the mailconfirmation
		this.processId = mglApp.ProcessId;

		const isProfiWerber = mglApp.FormModel.Profiwerbernummer && mglApp.FormModel.Profiwerbernummer.length > 0;

		if (!mglApp.FormModel.IstSchwerbehindert && mglApp.Tarif.Beitrag !== 0 && !isProfiWerber) {
			this.renderDirectDebit();
		}

		this.parent.afterRenderApp();

		if (this.appViewService.hasAdacPayProductItem()) {
			this.adacpayValidationService = new AppValidationService({ options: this.options }, this.appViewService.getAdacPayForm());
			this.appViewService.addConfirmationPageListener(this.onAdacPayItemClicked.bind(this));
		}

		T.Utils.Membership.getTaxInformation(this.options.app.apitaxinformation, {
			"Rate": mglApp.Tarif.TarifCode,
			"TMM": mglApp.FormModel.InAusbildung ? "JUE" : mglApp.FormModel.IstSchwerbehindert ? "SWB" : "",
			"PARField": this.successResponse.Partner != null,
			"JUVField": this.successResponse.JungeErwachsene != null ? this.successResponse.JungeErwachsene.length : 0
		}, this.onTaxInformationSuccess.bind(this), this.onTaxInformationError.bind(this));

		T.Utils.Store.set(SessionStorageKeys.RecommendationData, null, T.Utils.Store.SESSION);
		this.appPersistenceService.clearApplicationSession();
		if (this.options.app.iscooperation) {
			this.cooperationService.clearSession();
		}

		if (this.options.app.hasfdlmodule) {
			this.registerFdlModule();
		}

		S.Lazy.init();
		AppUtils.View.scrollTop();
		AppUtils.View.stopLoader();
	}

	private registerFdlModule(): void {
		const applicationModel = this.appPersistenceService.getFdlApplication();
		const creditCardPackages = this.appPersistenceService.getCreditCardPackageDataFromStore();
		const applicationInformation = this.fetchApplicationInformation();
		const externalProductKey = this.appPersistenceService.getExternalProductKey();
		const pkVid = this.piwikTrackingService.getLinkerParamFromDatalayer();
		const werbeschluessel = applicationModel.AdvertisingKey ?? this.options.app.advertisingkey;

		const encryptionCallParams = AppModelAdapter.getEncryptionCallParams(
			externalProductKey,
			applicationModel.Register,
			werbeschluessel != null ? werbeschluessel.toString() : '',
			creditCardPackages,
			this.successResponse.Token,
			pkVid
		);

		const url = this.getEncryptionApiUrl(applicationInformation);
		const apiUrl = AppUtils.Url.updateUrlParameter(encryptionCallParams, this.applicationUrlBuilder.fullUrl(url));

		this.appApiService.makeEncryptionCall(
			apiUrl,
			this.onEncryptionCallSuccess.bind(this),
			this.onEncryptionCallError.bind(this)
		);
	}

	private getEncryptionApiUrl(applicationInformation: any): string {
		return applicationInformation?.custom?.requestJweTokenApi || this.options.app.apicreditcardencryption;
	}

	private onEncryptionCallSuccess(data: ApiResponse<string>): void {
		const applicationInformation = this.fetchApplicationInformation();
		if (applicationInformation?.custom?.requestJweTokenApi && applicationInformation.custom.targetBankUrl) {
			this.appViewService.mapDataToSolarisForm(data.Data, applicationInformation.custom.targetBankUrl);
			this.appViewService.registerBankAccountFormSubmitListener(this.onTriggerTargetBank.bind(this));
		} else {
			this.appViewService.setRedirectLinkUrl(data.Data);
		}
	}

	private onEncryptionCallError(jqxhr: JQueryXHR, apiUrl: string): void {
		AppUtils.Helper.logError(jqxhr, 'MembershipApplication', window.location.href, `_makeEncryptionCall: ${apiUrl}`);
		T.Utils.View.stopLoader();
	}

	private onTriggerTargetBank(e: JQuery.Event): void {
		e.preventDefault();
		this.appViewService.triggerTargetBankFormSubmit();
	}

	private fetchApplicationInformation(): ApplicationMglInformationModel {
		const applicationInformation = this.mglConnectionService.retrieveApplicationInformation();
		this.options.app.processUrl = applicationInformation.config.processUrl;
		return applicationInformation;
	}

	private onWalletQrCodeSuccess(response: ApiResponse<QrCodeResponseModelApi>): void {
		if (response && response.Data) {
			this.appViewService.setQrCode(response.Data.ContentType, response.Data.Content);
		}
	}

	private onWalletAppUrlSuccess(response: ApiResponse<UrlResponseModelApi>): void {
		if (response && response.Data) {
			this.appViewService.setWalletAppUrl(response.Data.Url);
		}

		this.sendMailConfirmation();
	}

	private onWalletAppUrlDataError(): void {
		this.sendMailConfirmation();
	}

	private onTaxInformationSuccess(response: MglTaxInformationResponseModel): void {
		if (response.success && response.data) {
			this.appViewService.setTaxInformationToView(response.data);
		}
		this.checkWalletAndSendMailConfirmation();
	}

	private onTaxInformationError(): void {
		this.checkWalletAndSendMailConfirmation();
	}

	private checkWalletAndSendMailConfirmation(): void {
		if (this.appViewService.hasWalletBlock()) {
			T.Utils.Helper.updateUrlsWithDeviceParam();
			this.appApiService.getWalletQrCode(this.successResponse.Token, this.onWalletQrCodeSuccess.bind(this), () => {
				//NOSONAR
			}).done(
				() => {
					this.appApiService.getWalletAppUrl(this.successResponse.Token, this.onWalletAppUrlSuccess.bind(this), this.onWalletAppUrlDataError.bind(this));
				}
			);
		} else {
			this.sendMailConfirmation();
		}
	}

	private sendMailConfirmation(): void {
		const requestModel = AppModelAdapter.getConfirmationsMailModel(this.successResponse, this.appViewService.returnMailAttachments(), this.appViewService.getMailText(), this.appViewService.getHtmlMailText(), this.appViewService.getSubject(), this.appViewService.getMailAttatchments());
		requestModel.ProcessId = Number(this.processId);
		requestModel.HtmlFragmentMitglied = this.appViewService.returnMainContentForMember();
		requestModel.HtmlFragment = this.appViewService.returnMainContentForApi();
		this.appApiService.callApiMailConfirmation(requestModel);
	}

	private onAdacPayItemClicked(e: Event): void {
		e.preventDefault();
		if (this.adacpayValidationService.isValid()) {
			this.appApiService.createAdacPayToken({
				Wbs: this.options.app.advertisingkeyadacpay,
				Token: this.successResponse.Token
			}, this.adacPayTokenSuccess.bind(this), () => {
				//NOSONAR
			});
		}

	}

	private adacPayTokenSuccess(res: ApiResponse<string>): void {
		window.open(`${this.options.app.creditcardweburl}?${res.Data}`, "_blank");
	}

	private renderDirectDebit(): void {
		const token = this.successResponse.Token;
		if (token && !AppUtils.Helper.isEmptyString(token)) {
			this.bankAccountService.renderMandate(token, SparteType.SPARTE_MITGLIED, this.appViewService.getSepaAnchor(), 'SepaMandat', 60000, () => {
				this.appViewService.showSepaLinkWithInfo();
			});
		} else {
			this.appViewService.hideSepaNode();
		}
	}

}
